import React from 'react'
import PropTypes from 'prop-types'
import {
  ErrorMessage,
  FieldArray,
  Form,
  Formik,
} from 'formik'
import { withStyles } from '@material-ui/core/styles'
import {
  Button,
  IconButton,
  InputLabel,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  SvgIcon,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core'
import { mdiDelete, mdiPlus } from '@mdi/js'

import CancelButton from '../controls/buttons/CancelButton'
import DeleteButton from '../controls/buttons/DeleteButton'
import SubmitButton from '../controls/buttons/SubmitButton'
import ConfirmationDialog from '../controls/ConfirmationDialog'

const CREDENTIAL_TYPE = {
  CARD: 'Card',
  PIN: 'PIN',
  COMBO: 'Combo',
}

function hasNameError({ errors, touched }) {
  return Boolean(errors.name && touched.name)
}

function hasPinError({ errors, touched, index }) {
  return Boolean((
    touched.credentials
    && touched.credentials[`${index}`]
    && touched.credentials[`${index}`].pin
    && errors.credentials
    && errors.credentials[index]
    && errors.credentials[index].pin
  ))
}

function hasCardError({ errors, touched, index }) {
  return Boolean((
    touched.credentials
    && touched.credentials[`${index}`]
    && touched.credentials[`${index}`].card
    && errors.credentials
    && errors.credentials[index]
    && errors.credentials[index].card
  ))
}

function getCredentialType(credentials) {
  if (credentials.card !== '' && credentials.pin !== '') {
    return CREDENTIAL_TYPE.COMBO
  }

  if (credentials.card !== '') {
    return CREDENTIAL_TYPE.CARD
  }

  return CREDENTIAL_TYPE.PIN
}

function getCredentialsWithType(credentialsList) {
  return [...credentialsList].map((c) => ({
    ...c,
    type: getCredentialType(c),
  }))
}

function splitCard(card) {
  let cardBits = card.replace(/[\D]+/g, ' ').trim().split(' ')
  if (cardBits.length === 1 && cardBits[0].length === 8) {
    // In case there was no separator, and the length was exactly 8, split them 3:5
    cardBits = [card.substring(0, 3), card.substring(3)]
  }
  return cardBits
}

function isValidCard(card) {
  const cardBits = splitCard(card)
  if (cardBits.length !== 2) {
    return false
  }

  const facility = parseInt(cardBits[0], 10)
  const cardNum = parseInt(cardBits[1], 10)
  return facility >= 0 && facility <= 255 && cardNum >= 0 && cardNum <= 65535
}

function isValidPin(pin) {
  return /^\d{4,8}$/.test(pin)
}

const UserNameField = (p) => (
  <FormControl>
    <TextField
      name="name"
      error={hasNameError(p.formikProps)}
      type="text"
      label="Name"
      placeholder="Full Name"
      value={p.formikProps.values.name}
      onChange={p.formikProps.handleChange}
      onBlur={p.formikProps.handleBlur}
    />
    <ErrorMessage name="name">
      { (msg) => <div className={p.classes.error}>{msg}</div> }
    </ErrorMessage>
  </FormControl>
)

const UserStatusToggle = (p) => (
  <FormControlLabel
    label="User Enabled"
    className={p.classes.right}
    control={(
      <Switch
        name="enabled"
        checked={p.formikProps.values.enabled}
        color="primary"
        onChange={p.formikProps.handleChange}
      />
    )}
  />
)

const AddCredentialsButton = (p) => (
  <Button
    variant="outlined"
    onClick={() => p.fieldArrayProps.push({
      type: CREDENTIAL_TYPE.PIN,
      card: '',
      pin: '',
      enabled: true,
    })}
  >
    <SvgIcon className={p.classes.leftIcon}>
      <path d={mdiPlus} />
    </SvgIcon>
    Add Credentials
  </Button>
)

const CredentialsTypeField = (p) => (
  <FormControl className={p.classes.credFormControl}>
    <InputLabel htmlFor={`credentials.${p.index}.type`}>Type</InputLabel>
    <Select
      name={`credentials.${p.index}.type`}
      value={p.cred.type}
      onChange={p.handleChange}
      onBlur={p.handleBlur}
    >
      <MenuItem value={CREDENTIAL_TYPE.CARD}>{CREDENTIAL_TYPE.CARD}</MenuItem>
      <MenuItem value={CREDENTIAL_TYPE.PIN}>{CREDENTIAL_TYPE.PIN}</MenuItem>
      <MenuItem value={CREDENTIAL_TYPE.COMBO}>{CREDENTIAL_TYPE.COMBO}</MenuItem>
    </Select>
  </FormControl>
)

const CredentialsPinField = (p) => {
  if ([CREDENTIAL_TYPE.PIN, CREDENTIAL_TYPE.COMBO].includes(p.cred.type)) {
    return (
      <FormControl className={[p.classes.credFormControl, p.classes.credField].join(' ')}>
        <TextField
          name={`credentials.${p.index}.pin`}
          error={hasPinError(p)}
          type="text"
          label={CREDENTIAL_TYPE.PIN}
          placeholder="0000"
          value={p.cred.pin}
          onChange={p.handleChange}
          onBlur={p.handleBlur}
        />
        <ErrorMessage name={`credentials.${p.index}.pin`}>
          { (msg) => <div className={p.classes.error}>{msg}</div> }
        </ErrorMessage>
      </FormControl>
    )
  }

  return null
}

const CredentialsCardField = (p) => {
  if ([CREDENTIAL_TYPE.CARD, CREDENTIAL_TYPE.COMBO].includes(p.cred.type)) {
    return (
      <FormControl className={[p.classes.credFormControl, p.classes.credField].join(' ')}>
        <TextField
          name={`credentials.${p.index}.card`}
          error={hasCardError(p)}
          type="text"
          label={CREDENTIAL_TYPE.CARD}
          placeholder="000 1234"
          value={p.cred.card}
          onChange={p.handleChange}
          onBlur={p.handleBlur}
        />
        <ErrorMessage name={`credentials.${p.index}.card`}>
          { (msg) => <div className={p.classes.error}>{msg}</div> }
        </ErrorMessage>
      </FormControl>
    )
  }

  return null
}

const CredentialsRow = (p) => {
  const { cred, index } = p
  const credFieldProps = {
    errors: p.formikProps.errors,
    handleBlur: p.formikProps.handleBlur,
    handleChange: p.formikProps.handleChange,
    touched: p.formikProps.touched,
    classes: p.classes,
    cred,
    index,
  }

  return (
    <TableRow>
      { /* ========== Credential Fields ========== */ }
      <TableCell>
        <CredentialsTypeField {...credFieldProps} />
        <CredentialsPinField {...credFieldProps} />
        <CredentialsCardField {...credFieldProps} />
      </TableCell>

      { /* ========== Credential Enable/Disable Toggle ========== */ }
      <TableCell>
        <Switch
          name={`credentials.${index}.enabled`}
          checked={cred.enabled}
          color="primary"
          onChange={p.formikProps.handleChange}
        />
      </TableCell>

      { /* ========== Credential Delete Button ========== */ }
      <TableCell padding="none">
        <IconButton aria-label="Delete" onClick={() => p.fieldArrayProps.remove(index)}>
          <SvgIcon>
            <path d={mdiDelete} />
          </SvgIcon>
        </IconButton>
      </TableCell>
    </TableRow>
  )
}

const CredentialsList = (p) => {
  const { classes } = p.outerProps
  const { credentials } = p.formikProps.values

  return (
    <>
      <Table className={classes.credTable}>
        <TableHead>
          <TableRow>
            <TableCell>Access Credentials</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {
            !credentials.length
              ? (
                <TableRow>
                  <TableCell colSpan="3" className={classes.error}>
                    The user must have at least one set of credentials
                  </TableCell>
                </TableRow>
              )
              : credentials.map((cred, index) => (
                <CredentialsRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={`cred-${index}`}
                  formikProps={p.formikProps}
                  fieldArrayProps={p.fieldArrayProps}
                  classes={classes}
                  cred={cred}
                  index={index}
                />
              ))
          }
        </TableBody>
      </Table>
      <AddCredentialsButton fieldArrayProps={p.fieldArrayProps} classes={classes} />
    </>
  )
}

const FormButtons = (p) => {
  const { classes } = p.outerProps

  return (
    <div className={classes.actionButtonBar}>
      { /* ========== Delete User ========== */ }
      {
        p.outerProps.doorAccess.id && (
          <DeleteButton onClick={p.onDelete}>
            Delete User
          </DeleteButton>
        )
      }

      { /* ========== Save User ========== */ }
      <SubmitButton
        className={classes.rightButton}
        onClick={p.formikProps.handleSubmit}
        disabled={p.formikProps.isSubmitting}
      >
        Save
      </SubmitButton>

      { /* ========== Cancel ========== */ }
      <CancelButton className={classes.rightButton} onClick={p.onEditCancel}>
        Cancel
      </CancelButton>
    </div>
  )
}

class DoorAccessForm extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    doorAccess: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onEditCancel: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
  }

  static defaultProps = {
    doorAccess: {
      name: '',
      credentials: [],
      enabled: true,
      parent_community_id: 0,
    },
  }

  static styles = (theme) => ({
    credTable: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        minWidth: 425,
      },
    },
    credFieldFooterCell: {
      borderBottomWidth: 0,
    },
    credFormControl: {
      width: 100,
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    credField: {
      display: 'inline-block',
    },
    error: {
      color: 'red',
    },
    formSection: {
      margin: theme.spacing(0, 0, 2, 0),
      display: 'block',
    },
    formLabel: {
      display: 'block',
    },
    leftIcon: {
      marginRight: theme.spacing(1),
    },
    right: {
      float: 'right',
    },
    rightButton: {
      float: 'right',
      marginLeft: theme.spacing(1),
    },
    actionButtonBar: {
      overflow: 'hidden', // resolves div height to floating content instead of 0
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  })

  state = {
    showConfirmationDialog: false,
  }

  handleSave = (values) => {
    const { doorAccess } = this.props

    const credentials = values.credentials.map((c) => {
      const { type, ...creds } = c
      return {
        ...creds,
        pin: type === CREDENTIAL_TYPE.CARD ? '' : c.pin,
        card: type === CREDENTIAL_TYPE.PIN ? '' : c.card,
      }
    })

    const saveObj = {
      ...doorAccess,
      ...values,
      credentials,
    }

    this.props.onSave(saveObj)
  }

  handleDeleteButtonClick = () => {
    this.setState({ showConfirmationDialog: true })
  }

  handleDeleteConfirmed = () => {
    this.props.onDelete()
    this.handleConfirmationClose()
  }

  handleConfirmationClose = () => {
    this.setState({ showConfirmationDialog: false })
  }

  validateForm = (values) => {
    const errors = {}

    if (!values.name.trim()) {
      errors.name = 'Name Required'
    }

    if (values.credentials.length === 0) {
      errors.noCredentials = true
    }

    values.credentials.forEach((c, index) => {
      const validCard = isValidCard(c.card)
      const validPin = isValidPin(c.pin)
      let pinError
      let cardError

      if ([CREDENTIAL_TYPE.PIN, CREDENTIAL_TYPE.COMBO].includes(c.type)) {
        if (!validPin) {
          pinError = 'Invalid PIN'
        }

        if (!c.pin) {
          pinError = 'PIN Required'
        }
      }

      if ([CREDENTIAL_TYPE.CARD, CREDENTIAL_TYPE.COMBO].includes(c.type)) {
        if (!validCard) {
          cardError = 'Invalid Card'
        }

        if (!c.card) {
          cardError = 'Card Required'
        }
      }

      if (pinError || cardError) {
        errors.credentials = errors.credentials || []
        errors.credentials[index] = {
          pin: pinError,
          card: cardError,
        }
      }
    })

    return errors
  }

  renderDoorAccessForm = (formikProps) => (
    <Form>
      <UserNameField formikProps={formikProps} classes={this.props.classes} />
      <UserStatusToggle formikProps={formikProps} classes={this.props.classes} />
      <FieldArray
        name="credentials"
        render={(fieldArrayProps) => (
          <CredentialsList
            formikProps={formikProps}
            fieldArrayProps={fieldArrayProps}
            outerProps={this.props}
          />
        )}
      />
      <FormButtons
        formikProps={formikProps}
        outerProps={this.props}
        onDelete={this.handleDeleteButtonClick}
        onEditCancel={this.props.onEditCancel}
      />
    </Form>
  )

  render() {
    const { doorAccess } = this.props
    const initialCredentials = getCredentialsWithType(doorAccess.credentials)

    return (
      <>
        <Formik
          initialValues={{
            name: doorAccess.name,
            enabled: doorAccess.enabled,
            credentials: initialCredentials,
          }}
          onSubmit={this.handleSave}
          validate={this.validateForm}
          render={this.renderDoorAccessForm}
        />

        { this.state.showConfirmationDialog && (
          <ConfirmationDialog
            open
            title="Confirm User Deletion"
            description={`Are you sure you want to delete ${doorAccess.name}?`}
            confirmText="Delete"
            onCancel={this.handleConfirmationClose}
            onConfirm={this.handleDeleteConfirmed}
          />
        )}
      </>
    )
  }
}

export default withStyles(DoorAccessForm.styles)(DoorAccessForm)
